import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useShippingPolicy } from '@/lib/hooks/useShippingPolicy';
import { formatCurrency } from '@/lib/utils';
import { storeAtom } from '@/states/store.state';
import { useAtomValue } from 'jotai';
import { InfoIcon, Truck } from 'lucide-react';

interface ShippingHeaderInfosProps {
  dealId: string;
}

export function ShippingHeaderInfos({ dealId }: ShippingHeaderInfosProps) {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const { shippingPolicy } = useShippingPolicy(dealId);

  const OneOptionEnabled =
    shippingPolicy.minimumOrderEnabled || shippingPolicy.timeLimitedEnabled;
  const bothOptionsEnabled =
    shippingPolicy.minimumOrderEnabled && shippingPolicy.timeLimitedEnabled;
  const allShippingFeesZero = shippingPolicy.allShippingFeesZero;
  const isPolicyActive =
    OneOptionEnabled || bothOptionsEnabled || allShippingFeesZero;

  const {
    minimumOrderEnabled,
    timeLimitedEnabled,
    minimumOrderAmount,
    offerDuration,
  } = shippingPolicy;

  const noShippingText = t('ui-v2.settings.blocks-shipping-policy.no-shipping');

  const TriggerText = allShippingFeesZero
    ? t(
        'ui-v2.settings.blocks-shipping-policy.free-shipping.no-conditions.trigger'
      )
    : bothOptionsEnabled
      ? t(
          'ui-v2.settings.blocks-shipping-policy.free-shipping.both-options-enabled-trigger',
          {
            duration: offerDuration,
            minimum: formatCurrency(minimumOrderAmount, store),
          }
        )
      : minimumOrderEnabled
        ? t(
            'ui-v2.settings.blocks-shipping-policy.free-shipping.minimum-only-trigger',
            {
              minimum: formatCurrency(minimumOrderAmount, store),
            }
          )
        : timeLimitedEnabled
          ? t(
              'ui-v2.settings.blocks-shipping-policy.free-shipping.duration-only-trigger',
              {
                duration: offerDuration,
              }
            )
          : noShippingText;

  const TooltipText = allShippingFeesZero
    ? t(
        'ui-v2.settings.blocks-shipping-policy.free-shipping.no-conditions.description',
        {
          currency: formatCurrency(0, store),
        }
      )
    : bothOptionsEnabled
      ? t(
          'ui-v2.settings.blocks-shipping-policy.free-shipping.both-options-enabled-tooltip',
          {
            minimum: formatCurrency(minimumOrderAmount, store),
            duration: offerDuration,
          }
        )
      : minimumOrderEnabled
        ? t(
            'ui-v2.settings.blocks-shipping-policy.free-shipping.minimum-only',
            {
              minimum: formatCurrency(minimumOrderAmount, store),
            }
          )
        : timeLimitedEnabled
          ? t(
              'ui-v2.settings.blocks-shipping-policy.free-shipping.duration-only',
              {
                duration: offerDuration,
              }
            )
          : noShippingText;

  return (
    <div data-user-guide="Shipping Policy">
      {isPolicyActive ? (
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="hover:text-bold flex cursor-pointer items-center gap-1 rounded-sm hover:bg-gray-50">
                  <Truck className="h-4 w-4" />

                  <Link to={`/deal/${dealId}/settings`}>
                    <p>{TriggerText}</p>
                  </Link>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={10}>
                <div className="text-center">{TooltipText}</div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ) : (
        <div className="flex items-center gap-1 rounded-sm text-red-400 hover:cursor-pointer hover:bg-gray-50 hover:text-red-700">
          <InfoIcon className="h-4 w-4 text-red-500" />

          <Link to={`/deal/${dealId}/settings`}>
            <p>{TooltipText}</p>
          </Link>
        </div>
      )}
    </div>
  );
}
